import React, { useCallback, useEffect, useState } from "react";
import { caApi } from "../../../backend_urls/caApi";
import DocumentRequestsHeader from "./models/client_followups/DocumentRequestsHeader";
import CreateDocumentRequestModal from "./models/client_followups/CreateDocumentRequestModal";
import DocumentRequestsList from "./models/client_followups/DocumentRequestsList";
import ViewDocRequest from "./models/client_followups/ViewDocRequest";
import LoadingSpinner from "../../../components/loaders/LoadingSpinner";

const getErrorMessage = (error, fallback = "Something went wrong.") => {
    const data = error?.response?.data;

    if (!data) { return fallback; }
    if (typeof data === "string") { return data; }
    if (typeof data.detail === "string") { return data.detail; }
    /*
     * Handle normal Django REST Framework validation errors:
     *
     * {
     *     "field": ["Error message"]
     * }
     */
    if (typeof data === "object" && !Array.isArray(data)) {
        const messages = [];

        Object.entries(data).forEach(
            ([field, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((message) => {
                        messages.push(
                            `${field}: ${message}`
                        );
                    });
                } else if (typeof value === "string") {
                    messages.push(`${field}: ${value}`);
                }
            }
        );

        if (messages.length > 0) { return messages.join(" "); }
    }

    return fallback;
};

const normalizeListResponse = (response) => {
    const responseData = response?.data;

    /*
     * Standard DRF paginated response:
     *
     * {
     *     count,
     *     next,
     *     previous,
     *     results: []
     * }
     */
    if (
        responseData &&
        !Array.isArray(responseData) &&
        Array.isArray(responseData.results)
    ) {
        return responseData.results;
    }

    if (Array.isArray(responseData)) {
        return responseData;
    }

    return [];
};

/*
 * ---------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------
 */

const CANotificationsPage = () => {
    /*
     * -----------------------------------------------------------------------
     * Document request list state
     * -----------------------------------------------------------------------
     */

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    /*
     * -----------------------------------------------------------------------
     * Create modal
     * -----------------------------------------------------------------------
     */

    const [showForm, setShowForm] = useState(false);

    /*
     * -----------------------------------------------------------------------
     * Drawer state
     * -----------------------------------------------------------------------
     */

    const [drawer, setDrawer] = useState({
        open: false,
        loading: false,
        request: null,
    });

    /*
     * -----------------------------------------------------------------------
     * Mutation state
     * -----------------------------------------------------------------------
     *
     * Keeping mutation state separate from list loading prevents the whole
     * page from behaving like it is loading whenever a request is updated.
     */

    const [deletingRequestId, setDeletingRequestId] =
        useState(null);

    /*
     * -----------------------------------------------------------------------
     * FETCH DOCUMENT REQUESTS
     * -----------------------------------------------------------------------
     */

    const fetchData = useCallback(
        async ({
            showLoader = true,
        } = {}) => {
            try {
                if (showLoader) {
                    setLoading(true);
                }

                setError("");

                const response =
                    await caApi.getAllClientDocumentRequests();

                const requests =
                    normalizeListResponse(
                        response
                    );

                setData(requests);

                return requests;
            } catch (requestError) {
                console.error(
                    "Error fetching document requests:",
                    requestError
                );

                setError(
                    getErrorMessage(
                        requestError,
                        "Unable to load document requests."
                    )
                );

                setData([]);

                throw requestError;
            } finally {
                if (showLoader) {
                    setLoading(false);
                }
            }
        },
        []
    );

    /*
     * -----------------------------------------------------------------------
     * INITIAL LOAD
     * -----------------------------------------------------------------------
     */

    useEffect(() => {
        fetchData().catch(() => {
            /*
             * fetchData already handles and stores the error.
             *
             * Catching here prevents an unhandled promise rejection.
             */
        });
    }, [fetchData]);

    /*
     * -----------------------------------------------------------------------
     * OPEN CREATE MODAL
     * -----------------------------------------------------------------------
     */

    const openCreateForm = () => {
        setError("");
        setShowForm(true);
    };

    /*
     * -----------------------------------------------------------------------
     * CLOSE CREATE MODAL
     * -----------------------------------------------------------------------
     */

    const closeCreateForm = () => {
        setShowForm(false);
    };

    /*
     * -----------------------------------------------------------------------
     * CREATE DOCUMENT REQUEST
     * -----------------------------------------------------------------------
     *
     * CreateDocumentRequestModal calls this after a successful POST.
     *
     * We refresh the list here so the new request immediately appears.
     */

    const handleCreateRequest = async () => {
        try {
            setError("");

            await fetchData({
                showLoader: false,
            });

            setShowForm(false);
        } catch (requestError) {
            /*
             * fetchData already stores the error.
             *
             * Keep the modal open if refreshing fails. This avoids giving
             * the impression that the operation completed successfully
             * when the UI could not synchronize with the backend.
             */
            console.error(
                "Error refreshing after document request creation:",
                requestError
            );
        }
    };

    /*
     * -----------------------------------------------------------------------
     * VIEW / RETRIEVE DOCUMENT REQUEST
     * -----------------------------------------------------------------------
     */

    const handleViewRequest = async (
        clientId,
        docRequestId
    ) => {
        try {
            setError("");

            setDrawer({
                open: true,
                loading: true,
                request: null,
            });

            const response =
                await caApi.retrieveClientDocumentRequest(
                    clientId,
                    docRequestId
                );

            setDrawer({
                open: true,
                loading: false,
                request: response?.data || null,
            });
        } catch (requestError) {
            console.error(
                "Error retrieving document request:",
                requestError
            );

            setDrawer({
                open: false,
                loading: false,
                request: null,
            });

            setError(
                getErrorMessage(
                    requestError,
                    "Unable to load document request."
                )
            );
        }
    };

    /*
     * -----------------------------------------------------------------------
     * UPDATE DOCUMENT REQUEST
     * -----------------------------------------------------------------------
     *
     * This is especially important for the redesigned reminder system.
     *
     * ViewDocRequest can now send changes to:
     *
     * - request details
     * - due date
     * - expiry
     * - reminder configuration
     * - reminder schedules
     * - delivery channels
     * - templates
     * - custom messages
     * - reminder status
     *
     * The backend response becomes the source of truth for the drawer.
     */

    const handleUpdateRequest = async (
        clientId,
        docRequestId,
        payload
    ) => {
        try {
            setError("");

            setDrawer((previous) => ({
                ...previous,
                loading: true,
            }));

            const response =
                await caApi.updateClientDocumentRequest(
                    clientId,
                    docRequestId,
                    payload
                );

            const updatedRequest =
                response?.data || null;

            /*
             * Immediately synchronize the open drawer with the backend.
             */
            setDrawer({
                open: true,
                loading: false,
                request: updatedRequest,
            });

            /*
             * Refresh the list without displaying the global loading
             * spinner. The drawer already provides the immediate feedback.
             */
            try {
                await fetchData({
                    showLoader: false,
                });
            } catch (refreshError) {
                /*
                 * The actual update succeeded. If the list refresh fails,
                 * keep the drawer synchronized and report the refresh issue.
                 */
                console.error(
                    "Document request updated but list refresh failed:",
                    refreshError
                );
            }

            return updatedRequest;
        } catch (requestError) {
            console.error(
                "Error updating document request:",
                requestError
            );

            setDrawer((previous) => ({
                ...previous,
                loading: false,
            }));

            setError(
                getErrorMessage(
                    requestError,
                    "Unable to update document request."
                )
            );

            /*
             * ViewDocRequest may use this rejection to display its own
             * validation/save error as well.
             */
            throw requestError;
        }
    };

    /*
     * -----------------------------------------------------------------------
     * DELETE DOCUMENT REQUEST
     * -----------------------------------------------------------------------
     */

    const handleDeleteRequest = async (clientId, docRequestId) => {
        /*
         * Prevent accidental double-clicks.
         */
        if (deletingRequestId === docRequestId) { return; }

        try {
            setDeletingRequestId(docRequestId);

            setError("");

            await caApi.destroyClientDocumentRequest(
                clientId,
                docRequestId
            );

            /*
             * Close drawer if the deleted request is currently open.
             *
             * Compare as strings because UUIDs / IDs can sometimes arrive
             * in different primitive forms.
             */
            const openRequestId = drawer.request?.id;

            if (
                openRequestId != null &&
                String(openRequestId) === String(docRequestId)
            ) {
                setDrawer({ open: false, loading: false, request: null });
            }

            /*
             * Refresh without the global spinner.
             */
            await fetchData({showLoader: false});
        } catch (requestError) {
            console.error(
                "Error deleting document request:",
                requestError
            );

            setError(
                getErrorMessage(
                    requestError,
                    "Unable to delete document request."
                )
            );
        } finally { setDeletingRequestId(null); }
    };

    /*
     * -----------------------------------------------------------------------
     * CLOSE DRAWER
     * -----------------------------------------------------------------------
     */
    const closeDrawer = () => {
        setDrawer({ open: false, loading: false, request: null });
    };

    /*
     * -----------------------------------------------------------------------
     * CLEAR PAGE ERROR
     * -----------------------------------------------------------------------
    */
    const clearError = () => { setError(""); };

    /*
    * -----------------------------------------------------------------------
    * RENDER
    * -----------------------------------------------------------------------
    */

    return (
        <div className="ca-notifications-page">
            {/* =============================================================
                GLOBAL LOADING
            ============================================================= */}
            {loading && ( <LoadingSpinner /> )}

            {/* =============================================================
                PAGE ERROR
            ============================================================= */}
            {error && (
                <div
                    className = "ca-notifications-error"
                    role      = "alert"
                >
                    <span>{error}</span>

                    <button
                        type       = "button"
                        onClick    = { clearError }
                        aria-label = "Dismiss error"
                    >
                        ×
                    </button>
                </div>
            )}

            {/* =============================================================
                HEADER
            ============================================================= */}
            <DocumentRequestsHeader onCreate = { openCreateForm } />

            {/* =============================================================
                CREATE DOCUMENT REQUEST
            ============================================================= */}
            {showForm && (
                <CreateDocumentRequestModal
                    onClose = { closeCreateForm }
                    onSave  = { handleCreateRequest }
                />
            )}

            {/* =============================================================
                DOCUMENT REQUEST LIST
            ============================================================= */}
            <DocumentRequestsList
                data              = { data }
                onView            = { handleViewRequest }
                onDelete          = { handleDeleteRequest }
                deletingRequestId = { deletingRequestId }
            />

            {/* =============================================================
                DOCUMENT REQUEST DETAILS / EDIT DRAWER
            ============================================================= */}
            <ViewDocRequest
                isOpen   = { drawer.open }
                loading  = { drawer.loading }
                request  = { drawer.request }
                onClose  = { closeDrawer }
                onUpdate = { handleUpdateRequest }
            />
        </div>
    );
};

export default CANotificationsPage;
